import { useEffect, useId, useRef } from 'react'

import { TONE_TILE, isDestructive } from '../../lib/alerts'

/** The design's 24×24 stroke icons for the dialog tile, by `spec.icon`. */
const ICON_PATH = {
  warning: 'M12 8v5M12 16.5h.01M10.3 3.9L2.7 17a2 2 0 0 0 1.7 3h15.2a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
  card: 'M2 8h20M2 6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z',
  refresh: 'M21 12a9 9 0 1 1-3-6.7M21 4v5h-5',
  trash: 'M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13',
  flame: 'M12 3c0 3-4 4-4 8a4 4 0 0 0 8 0c0-1.5-1-3-1-3s2 1.5 2 4a5.5 5.5 0 0 1-11 0c0-5 6-6 6-9z',
  power: 'M12 3v9M6.3 6.3a9 9 0 1 0 11.4 0',
}

export function AlertGlyph({ icon, size = 21 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={ICON_PATH[icon] ?? ICON_PATH.warning} />
    </svg>
  )
}

/**
 * The modal dialog of design page 16: a scrim, a 22px card, a 44px tinted
 * icon tile, the title, the body, an optional note, a 52px primary and a
 * 48px borderless cancel. The primary is solid red only for a destructive
 * spec (`lib/alerts.js`); otherwise the brand.
 *
 * It closes on cancel, on confirm and on Escape — not on a tap on the scrim,
 * as the design says — and it goes with the page that opened it, so a
 * navigation never leaves a question hanging over a different screen.
 */
export default function TootiDialog({ spec, onConfirm, onDismiss }) {
  const titleId = useId()
  const primaryRef = useRef(null)

  useEffect(() => {
    primaryRef.current?.focus()
  }, [])

  useEffect(() => {
    function onKey(event) {
      if (event.key === 'Escape') onDismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onDismiss])

  const destructive = isDestructive(spec)

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[rgba(8,15,24,0.55)] p-6" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex w-full max-w-sm flex-col gap-[13px] rounded-[22px] bg-surface p-[22px] shadow-[0_20px_46px_rgba(0,0,0,0.34)]"
      >
        <span className={`grid size-11 place-items-center rounded-[13px] ${TONE_TILE[spec.tone] ?? TONE_TILE.brand}`}>
          <AlertGlyph icon={spec.icon} />
        </span>
        <h2 id={titleId} className="text-[17.5px] font-bold leading-[1.6] text-ink">
          {spec.title}
        </h2>
        <p className="text-[13px] leading-[1.95] text-ink-2">{spec.body}</p>
        {spec.note && (
          <p className="rounded-xl bg-surface-2 px-3 py-[11px] text-[11.5px] leading-[1.85] text-ink-3">{spec.note}</p>
        )}
        <div className="flex flex-col gap-2 pt-1.5">
          <button
            ref={primaryRef}
            type="button"
            onClick={onConfirm}
            className={`h-[52px] rounded-[13px] text-[14.5px] font-bold transition-colors ${
              destructive ? 'bg-bad text-white hover:bg-bad-ink' : 'bg-brand-600 text-white hover:bg-brand-700 dark:text-slate-900'
            }`}
          >
            {spec.confirmLabel}
          </button>
          {spec.cancelLabel && (
            <button type="button" onClick={onDismiss} className="h-12 rounded-[13px] text-[13.5px] font-semibold text-ink-2 hover:bg-surface-2">
              {spec.cancelLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
